import { useEffect, useState, useCallback } from "react";
import api, { formatDate } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, Building2, Package, MessageSquare, Inbox } from "lucide-react";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";

const TABS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "converted", label: "Converted" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

const STATUS_STYLE = {
  new: "bg-[#FCE9E4] text-[#C84B31]",
  contacted: "bg-[#FCECD8] text-[#915A1A]",
  converted: "bg-[#EAF2ED] text-[#2D523B]",
  archived: "bg-[#E5E3DB] text-[#68665E]",
};

export default function Leads() {
  const [tab, setTab] = useState("new");
  const [data, setData] = useState({ leads: [], counts: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads", { params: { status } });
      setData(data);
    } catch { /* ignore */ }
    setLoading(false);
    window.dispatchEvent(new Event("leads-changed"));
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  async function setStatus(id, status) {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Marked ${status}`);
      load(tab);
    } catch { toast.error("Could not update"); }
  }

  const counts = data.counts || {};
  const paged = usePagination(data.leads, 25);

  return (
    <div data-testid="leads-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Sales</div>
        <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Website Leads</h1>
        <p className="text-sm text-[#68665E] mt-2">
          Callback & quote requests submitted from the public website ·{" "}
          <span className="font-medium text-[#1A1A18]" data-testid="leads-new-count">{counts.new || 0} new</span>
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`leads-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              tab === t.key ? "bg-[#1A1A18] text-white" : "bg-white border border-[#E5E3DB] text-[#68665E] hover:text-[#1A1A18]"
            }`}>
            {t.label}
            {t.key !== "all" && counts[t.key] > 0 && (
              <span className="ml-2 text-xs opacity-70">{counts[t.key]}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#68665E] text-sm" data-testid="leads-loading">Loading leads…</div>
      ) : data.leads.length === 0 ? (
        <div className="card-surface p-12 text-center" data-testid="leads-empty">
          <Inbox className="w-10 h-10 text-[#C84B31]/40 mx-auto mb-3" />
          <div className="font-medium text-[#1A1A18]">No {tab === "all" ? "" : tab} leads yet</div>
          <p className="text-sm text-[#68665E] mt-1">Enquiries from the website's "Get a quote" form land here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <Pagination {...paged} testId="leads-pagination" />
          {paged.pageItems.map((l) => (
            <div key={l.id} data-testid={`lead-card-${l.id}`} className="card-surface p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-headings text-lg font-bold text-[#1A1A18]">{l.name}</span>
                    <span className={`px-2 py-0.5 text-[11px] uppercase tracking-wide rounded-full font-semibold ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>
                      {l.status}
                    </span>
                    <span className="text-xs text-[#68665E]">{formatDate(l.created_at)}</span>
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                    <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-[#1A1A18] hover:text-[#C84B31]">
                      <Phone className="w-4 h-4 text-[#C84B31]" /> {l.phone}
                    </a>
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-[#1A1A18] hover:text-[#C84B31] break-all">
                        <Mail className="w-4 h-4 text-[#C84B31]" /> {l.email}
                      </a>
                    )}
                    {l.company && (
                      <span className="flex items-center gap-2 text-[#68665E]"><Building2 className="w-4 h-4 text-[#C84B31]" /> {l.company}</span>
                    )}
                    {l.cargo && (
                      <span className="flex items-center gap-2 text-[#68665E]"><Package className="w-4 h-4 text-[#C84B31]" /> {l.cargo}</span>
                    )}
                  </div>
                  {l.message && (
                    <div className="mt-3 flex items-start gap-2 text-sm text-[#4F4C44] bg-[#F5F4F0] rounded-lg p-3">
                      <MessageSquare className="w-4 h-4 text-[#C84B31] mt-0.5 shrink-0" /> {l.message}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {l.status !== "contacted" && (
                    <button onClick={() => setStatus(l.id, "contacted")} data-testid={`lead-contacted-${l.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E3DB] text-[#915A1A] hover:bg-[#FCECD8]">
                      Mark contacted
                    </button>
                  )}
                  {l.status !== "converted" && (
                    <button onClick={() => setStatus(l.id, "converted")} data-testid={`lead-converted-${l.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E3DB] text-[#2D523B] hover:bg-[#EAF2ED]">
                      Mark converted
                    </button>
                  )}
                  {l.status !== "archived" && (
                    <button onClick={() => setStatus(l.id, "archived")} data-testid={`lead-archived-${l.id}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[#E5E3DB] text-[#68665E] hover:bg-[#E5E3DB]">
                      Archive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
